/**
 * The queue.
 *
 * Matchmaking widens the accepted rating band over elapsed wait (spec 9), and
 * after ~8 seconds of a thin queue the server pairs you with a recorded run
 * near your rating instead. That fallback is invisible on purpose — invariant 1
 * makes a ghost the same event stream as a live seat, so this screen must never
 * say anything that would let a player guess which they got. It shows a band
 * widening and a wait time, and then a match starts. That is all it may show.
 *
 * THE GROUP QUEUE ALMOST BROKE THAT. A group can fail to fill, and a queue that
 * silently produces a two-player match instead reads as a bug — so it has to
 * say something. But "we will start you a duel against a recorded run" hands
 * the player the one fact this screen exists to withhold, and so does anything
 * they can derive it from: if the fallback were ALWAYS synthetic, watching the
 * clock run out would be enough.
 *
 * The resolution is in the matchmaker, not in this copy. Its fallback tries a
 * live partner first — a stranded group player and a stranded duel player are
 * paired with each other — so "starts either way" is now genuinely ambiguous
 * about what arrives. This screen may say WHEN something will happen. It still
 * may not say what.
 */

import { useEffect, useState } from 'react';
import {
  GAME_MODES,
  GHOST_FALLBACK_MS,
  HANDLE_MAX_LENGTH,
  PACES,
  TIERS,
  countryPromptIsClue,
} from '@geogame/shared';
import type { GameMode } from '@geogame/shared';

import { ConnectionBanner } from '../components/ConnectionBanner';
import { tierLabel } from '../lib/format';
import { useGame } from '../store/gameStore';

/** Mode names, so the lobby and the home screen call them the same things. */
const MODE_LABEL: Record<GameMode, string> = {
  country: 'Countries',
  city: 'Cities',
  state: 'US States',
};

/** Cosmetic mirror of the server's widening policy. */
function bandFor(elapsedMs: number): number {
  const steps = Math.floor(elapsedMs / 2_000);
  return Math.min(600, 75 + steps * 75);
}

export function QueueScreen(): React.JSX.Element {
  const lobby = useGame((s) => s.lobby);
  const queue = useGame((s) => s.queue);
  const tier = useGame((s) => s.tier);
  const gameMode = useGame((s) => s.gameMode);
  const status = useGame((s) => s.status);
  const rttMs = useGame((s) => s.rttMs);
  const cancelQueue = useGame((s) => s.cancelQueue);
  const lastError = useGame((s) => s.lastError);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const id = window.setInterval(() => setElapsedMs(Date.now() - started), 200);
    return () => window.clearInterval(id);
  }, []);

  const startLobby = useGame((s) => s.startLobby);
  const playerId = useGame((s) => s.playerId);
  const handle = useGame((s) => s.handle);
  const renameInLobby = useGame((s) => s.renameInLobby);
  const configureLobby = useGame((s) => s.configureLobby);
  const promoteInLobby = useGame((s) => s.promoteInLobby);
  const inviteToken = useGame((s) => s.inviteToken);
  const [setupOpen, setSetupOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // A LINK IS NOT A QUEUE, so it does not get a queue's screen. There is
  // nothing to estimate and no band to widen: the wait ends when somebody
  // presses the button, and the only fact worth showing is who is here.
  if (lobby !== null) {
    const mine = lobby.members.find((m) => m.playerId === playerId) ?? null;
    const isHost = lobby.hostId === playerId;
    const inviteUrl = inviteToken === null ? null : `${window.location.origin}/d/${inviteToken}`;
    return (
      <main className="gg-screen gg-scroll gg-lobby">
        <ConnectionBanner status={status} rttMs={rttMs} inMatch={false} />
        <div className="gg-queue">
          {/*
            ONE KIND OF LOBBY. It used to announce itself as a Duel or a Group
            depending on how many seats the link was minted for, which asked
            the person sending it to know how many friends would turn up --
            the one thing nobody knows at that moment. Every link now holds
            six and the table is sized by who is actually standing here when
            somebody presses start.
          */}
          <h1 className="gg-queue-title">Lobby</h1>
          <p className="gg-queue-sub">
            {MODE_LABEL[lobby.gameMode]} · {tierLabel(lobby.tier)} ·{' '}
            {lobby.members.length === 1 ? 'just you so far' : `${lobby.members.length} here`}
          </p>

          <ul className="gg-lobby-roster">
            {lobby.members.map((m) => (
              <li
                key={m.playerId}
                className="gg-lobby-member"
                data-you={m.playerId === playerId ? 'true' : 'false'}
              >
                {m.handle}
                {m.playerId === playerId ? <span className="gg-lobby-you">you</span> : null}
                {m.playerId === lobby.hostId ? (
                  <span className="gg-lobby-host">host</span>
                ) : isHost ? (
                  /*
                    Offered only to the host, and only for somebody else. The
                    host is whoever is willing to be asked what everybody is
                    playing, and on a long evening that stops being fun before
                    the evening does.
                  */
                  <button
                    type="button"
                    className="gg-lobby-promote"
                    onClick={() => promoteInLobby(m.playerId)}
                  >
                    Make host
                  </button>
                ) : null}
              </li>
            ))}
            {/*
              NO ROW OF EMPTY SEATS. Six "waiting…" placeholders under two
              friends read as five people who had not shown up yet, when the
              truth is that two is a perfectly good match. One line, and only
              while there is genuinely nobody else.
            */}
            {lobby.members.length === 1 ? (
              <li className="gg-lobby-member" data-empty="true">
                send the link below to bring somebody in
              </li>
            ) : null}
          </ul>

          {/*
            THE LINK, ON SCREEN THE WHOLE TIME.
            ================================================================
            It used to be handed over once, on the screen that minted it, and
            then vanish the moment you entered the lobby -- so bringing in a
            third person meant leaving, minting again, and abandoning the two
            people already waiting. The lobby is the thing you invite people
            INTO, so the invitation belongs in it.
          */}
          {inviteUrl !== null ? (
            <div className="gg-lobby-invite">
              <code className="gg-link">{inviteUrl}</code>
              <button
                type="button"
                className="gg-btn"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(inviteUrl)
                    .then(() => {
                      setCopied(true);
                      window.setTimeout(() => setCopied(false), 2_000);
                    })
                    .catch(() => undefined);
                }}
              >
                {copied ? 'Copied' : 'Copy invite link'}
              </button>
            </div>
          ) : null}

          {/*
            YOUR NAME, HERE, because for an invited player this is the first
            screen of the game they ever see. They arrive on a link, accept,
            and land in a roster of friends under `PolarFjord417` with nowhere
            to say otherwise -- and a table of anonymous handles is the one
            thing a lobby exists to prevent. Typing here renames you on
            everybody else's roster while they watch.
          */}
          <label className="gg-lobby-name">
            <span className="gg-lobby-name-label">Your name</span>
            <input
              className="gg-input"
              value={handle}
              maxLength={HANDLE_MAX_LENGTH}
              placeholder={mine?.handle ?? "Leave blank and we'll name you"}
              onChange={(e) => renameInLobby(e.target.value)}
              aria-label="Your name in this lobby"
            />
          </label>

          {/*
            AND WHAT YOU ARE ABOUT TO PLAY. The settings came off the link,
            which was right for one match and wrong for an evening: switching
            mode or difficulty used to mean everybody going home so one person
            could mint and redistribute a fresh link.
          */}
          <div className="gg-lobby-setup">
            {/*
              THE HOST CHOOSES, and everybody else is told.
              ==============================================================
              Any member used to be able to change these, which turned one
              screen six people were reading into a screen that changed under
              whoever was reading it. A non-host sees the same summary line
              without a control on it -- the information is not the problem.

              STARTING is deliberately still open to everybody: that is the
              thing which, put behind one person, means waiting for them to
              look at their phone.
            */}
            <button
              type="button"
              className="gg-setup"
              aria-label={isHost ? 'Change what this lobby plays' : 'What this lobby is playing'}
              aria-expanded={isHost ? setupOpen : undefined}
              disabled={!isHost}
              onClick={() => isHost && setSetupOpen((v) => !v)}
            >
              <span className="gg-setup-label">
                {MODE_LABEL[lobby.gameMode]} · {tierLabel(lobby.tier)}
                {lobby.pace === 'relaxed' ? ' · relaxed' : ''}
              </span>
              <span className="gg-setup-action">
                {isHost ? (setupOpen ? 'Done' : 'Change') : 'host chooses'}
              </span>
            </button>

            {setupOpen && isHost ? (
              <div className="gg-lobby-options">
                <div className="gg-course-row" role="radiogroup" aria-label="Game mode">
                  {GAME_MODES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      role="radio"
                      aria-checked={lobby.gameMode === m}
                      className="gg-chip"
                      data-selected={lobby.gameMode === m ? 'true' : 'false'}
                      onClick={() =>
                        configureLobby({
                          gameMode: m,
                          courseId: null,
                          // Expert is a Countries difficulty; see PlayScreen.
                          ...(m !== 'country' && countryPromptIsClue(lobby.tier)
                            ? { tier: 'cartographer' }
                            : {}),
                        })
                      }
                    >
                      {MODE_LABEL[m]}
                    </button>
                  ))}
                </div>
                <div className="gg-course-row" role="radiogroup" aria-label="Difficulty">
                  {TIERS.filter((t) => !countryPromptIsClue(t) || lobby.gameMode === 'country').map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={lobby.tier === t}
                      className="gg-chip"
                      data-selected={lobby.tier === t ? 'true' : 'false'}
                      onClick={() => configureLobby({ tier: t })}
                    >
                      {tierLabel(t)}
                    </button>
                  ))}
                </div>
                {/*
                  PACE, NEXT TO DIFFICULTY AND NOT INSIDE IT.
                  ==========================================================
                  How hard the questions are and how long you get to answer
                  them are different questions, and a group that wants hard
                  questions at a kind speed should be able to say so. One
                  clock for the whole table, because the room arms a round for
                  everybody at once.
                */}
                <div className="gg-course-row" role="radiogroup" aria-label="Pace">
                  {PACES.map((pc) => (
                    <button
                      key={pc}
                      type="button"
                      role="radio"
                      aria-checked={lobby.pace === pc}
                      className="gg-chip"
                      data-selected={lobby.pace === pc ? 'true' : 'false'}
                      onClick={() => configureLobby({ pace: pc })}
                    >
                      {pc === 'relaxed' ? 'Relaxed clock' : 'Normal clock'}
                    </button>
                  ))}
                </div>
                <p className="gg-note">
                  Anyone here can change this, and everybody sees it at once.
                </p>
              </div>
            ) : null}
          </div>

          {lastError ? <p className="gg-note gg-note-error">{lastError.message}</p> : null}

          <button type="button" className="gg-btn gg-btn-primary mt-4" onClick={startLobby}>
            {lobby.members.length > 1 ? 'Start' : 'Start anyway'}
          </button>
          <p className="gg-note">
            {lobby.members.length > 1
              ? 'Anyone here can start it.'
              : 'Send them the link. Starting alone plays a recorded run.'}
          </p>
          <button type="button" className="gg-btn mt-2" onClick={cancelQueue}>
            Leave
          </button>
        </div>
      </main>
    );
  }

  const band = bandFor(elapsedMs);
  const isGroup = queue?.kind === 'group';
  const fallbackAt = queue?.groupFallbackAtServerTs;
  const seats = queue?.groupTargetSeats ?? 6;
  // Counted down from an absolute server instant the `queued` frame carried, so
  // no queue-progress push channel has to exist for this one line of copy.
  const holdsForMs = fallbackAt === undefined ? null : Math.max(0, fallbackAt - Date.now());
  const progress = Math.min(
    1,
    isGroup && fallbackAt !== undefined && queue !== null
      ? elapsedMs / Math.max(1, fallbackAt - queue.startedAtServerTs)
      : elapsedMs / (GHOST_FALLBACK_MS * 2),
  );

  return (
    <main className="gg-screen gg-center">
      <ConnectionBanner status={status} rttMs={rttMs} inMatch={false} />

      <div className="gg-queue">
        <div className="gg-queue-pulse" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>

        <h1 className="gg-queue-title">{isGroup ? 'Finding a group' : 'Finding an opponent'}</h1>
        <p className="gg-queue-sub">
          {gameMode === 'city'
            ? 'Pin the City'
            : gameMode === 'state'
              ? 'Pin the State'
              : tierLabel(queue?.tier ?? tier)}{' '}
          ·{' '}
          {(elapsedMs / 1000).toFixed(0)}s
        </p>

        <div className="gg-queue-band">
          <div className="gg-queue-band-fill" style={{ width: `${progress * 100}%` }} />
        </div>
        <p className="gg-note tabular-nums">Rating band ±{band}</p>
        {isGroup ? (
          <p className="gg-note tabular-nums">
            Up to {seats} players
            {holdsForMs === null
              ? null
              : holdsForMs > 0
                ? ` · starts either way in ${Math.ceil(holdsForMs / 1000)}s`
                : ' · starting'}
          </p>
        ) : null}

        {lastError ? <p className="gg-note gg-note-error">{lastError.message}</p> : null}

        <button type="button" className="gg-btn mt-4" onClick={cancelQueue}>
          Cancel
        </button>
      </div>
    </main>
  );
}
